import { z } from 'zod';
import { StoredDecisionSchema } from './eligibility-rules.js';
/**
 * Insurance, pricing and the advisory AI estimate (M6, ADR-011).
 *
 * Three things live here and they are deliberately kept apart, because confusing any
 * two of them is how a clinic ends up charging the wrong amount:
 *
 * | Layer | Authority | Shape |
 * |---|---|---|
 * | **Eligibility** - what the payer said | the payer, via Stedi | `EligibilityStatusResponse` |
 * | **Price quote** - what the patient is told | the deterministic rule engine | `PriceQuote` |
 * | **AI estimate** - what a model guessed | nothing; advisory only | `CopayEstimate` |
 *
 * `PriceQuote` is the only one that ever becomes money (`charge_intents`, M7.3). The AI
 * estimate is an annotation next to it for staff, never an input to it (rules §55.36,
 * `AI_SYSTEM_SPEC.md` §1).
 *
 * ## No PHI crosses this file
 *
 * Every shape here is either non-identifying (a payer's display name, an amount in
 * cents, an i18n key) or a write *into* the platform (a member id on the way to an
 * encrypted column). Nothing that comes *back* out of the API carries a member id, a
 * date of birth or a parsed 271: `EligibilityStatusResponse` answers a status and a
 * quote, and that is the whole of what the funnel needs (`API.md` §2.4).
 *
 * ## Amounts are integer cents
 *
 * Everywhere, without exception. A 271 carries decimal strings and a model answers in
 * dollars; both are converted at their boundary. Nothing in the platform multiplies a
 * float by 100.
 */
/* ------------------------------------------------------------------- payers ---- */
/**
 * A payer as the booking funnel is allowed to see it (`FR-INS-01`).
 *
 * Three fields, and the omissions are the point: no `stediPayerId` routing detail
 * beyond the id the client has to echo back, no eligibility-capability flags, no
 * per-tenant sort order. An anonymous visitor gets a name to recognise and an id to
 * send back, because that is all a picker needs.
 */
export const PublicPayerSchema = z
    .object({
    /** The tenant's `payers_cache.id`. Opaque to the client. */
    id: z.uuid(),
    displayName: z.string().min(1).max(200),
    /** Alternate spellings, so the client can highlight why a row matched. */
    aliases: z.array(z.string().max(200)).max(20).default([]),
    logoUrl: z.url().max(2000).nullable().default(null),
})
    .strict();
export const PayerSearchQuerySchema = z
    .object({
    /** Free text. Below two characters the endpoint answers an empty list, not an error. */
    q: z.string().max(100).default(''),
    limit: z.coerce.number().int().min(1).max(50).default(20),
})
    .strict();
export const PayerSearchResponseSchema = z.object({ data: z.array(PublicPayerSchema) }).strict();
/** Curated payer rows as the clinic's staff see them (`GET /admin/payers`). */
export const AdminPayerSchema = PublicPayerSchema.extend({
    stediPayerId: z.string().min(1).max(64),
    supportsEligibility: z.boolean(),
    isVisible: z.boolean(),
    sortOrder: z.int(),
    lastSyncedAt: z.iso.datetime().nullable(),
}).strict();
/**
 * `POST /admin/payers/refresh` (M13.5).
 *
 * The body is optional and so is every field in it: an empty body means "rebuild the
 * whole curated list", which is what the nightly job sends and what the button on the
 * settings screen sends.
 *
 * `query` narrows the refresh to one search, for the case the sweep cannot serve: a
 * clinic whose patients keep naming a payer that never appears in the list. The vendor's
 * directory has no listing endpoint and caps a search at a hundred matches
 * (`INTEGRATIONS.md` §2), so "ask for it by name" is the remedy, and a narrowed refresh
 * deliberately hides nothing - it has seen one payer, not the directory.
 */
export const RefreshPayersRequestSchema = z
    .object({
    query: z.string().trim().min(2).max(100).optional(),
    /** Two-letter states to narrow the directory to. Defaults to the clinic's own. */
    states: z.array(z.string().length(2)).max(60).optional(),
})
    .strict();
/* -------------------------------------------------------------- eligibility ---- */
/**
 * What the patient's browser is allowed to know about a check in flight.
 *
 * Four states, not the seven `eligibility_checks.status` has. The database
 * distinguishes `data_error` from `payer_error` from `timeout` because staff need to
 * know which happened; the funnel does not, and telling an anonymous visitor that the
 * clinic's payer enrolment is wrong would be leaking an operational detail to answer a
 * question they did not ask (`FR-INS-04`).
 *
 * | Public | Database | What the patient sees |
 * |---|---|---|
 * | `pending` | `pending` | a progress state, for at most 15 s |
 * | `verified` | `active` / `inactive` | the quote |
 * | `failed` | `data_error` | a corrective hint (`hintKey`), because it *is* theirs to fix |
 * | `unavailable` | `payer_error` / `timeout` | "we will verify your coverage" |
 *
 * `inactive` maps to `verified`, not to `failed`: the check succeeded. What it found
 * is a pricing question, and the quote answers it.
 */
export const ELIGIBILITY_PUBLIC_STATUSES = [
    'pending',
    'verified',
    'failed',
    'unavailable',
];
export const EligibilityPublicStatusSchema = z.enum(ELIGIBILITY_PUBLIC_STATUSES);
/** `eligibility_checks.status` (`ck_eligibility_checks_status`). */
export const ELIGIBILITY_CHECK_STATUSES = [
    'pending',
    'active',
    'inactive',
    'data_error',
    'payer_error',
    'timeout',
];
export const EligibilityCheckStatusSchema = z.enum(ELIGIBILITY_CHECK_STATUSES);
export const SUBSCRIBER_RELATIONSHIPS = ['self', 'spouse', 'child', 'other'];
export const SubscriberRelationshipSchema = z.enum(SUBSCRIBER_RELATIONSHIPS);
/**
 * The policy holder, when that is not the patient.
 *
 * Required as a whole or absent as a whole: a 270 with a surname and no date of birth
 * is a request the payer answers with AAA 72 (`packages/integrations/stedi`), so half
 * a subscriber is worse than none.
 */
export const EligibilitySubscriberSchema = z
    .object({
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    /** ISO `YYYY-MM-DD` local calendar date, never an instant. */
    dateOfBirth: z.iso.date(),
})
    .strict();
/**
 * `POST /public/eligibility` (`API.md` §2.4).
 *
 * The draft is not named in the body: it is the `ha_draft` cookie, exactly as every
 * other draft route resolves it (`booking/public-drafts.controller.ts`). A `draftId`
 * in the body would be an id a stranger could try.
 */
export const StartEligibilityRequestSchema = z
    .object({
    /** `payers_cache.id` from the picker. */
    payerId: z.uuid(),
    memberId: z.string().min(2).max(64),
    groupNumber: z.string().max(64).optional(),
    relationship: SubscriberRelationshipSchema.default('self'),
    subscriber: EligibilitySubscriberSchema.optional(),
})
    .strict()
    .refine((value) => value.relationship === 'self' || value.subscriber !== undefined, {
    message: 'A policy held by somebody else needs the policy holder.',
    path: ['subscriber'],
});
export const StartEligibilityResponseSchema = z
    .object({
    data: z
        .object({
        checkId: z.uuid(),
        status: EligibilityPublicStatusSchema,
        /** Milliseconds the client should keep polling before giving up (`FR-INS-04`). */
        pollBudgetMs: z.int().positive(),
    })
        .strict(),
})
    .strict();
/* ------------------------------------------------------------- price quotes ---- */
/** `price_quotes.kind` (`ARCHITECTURE.md` §9.3). */
export const PRICE_QUOTE_KINDS = ['self_pay', 'copay_estimate', 'pending_review'];
export const PriceQuoteKindSchema = z.enum(PRICE_QUOTE_KINDS);
/**
 * Every explanation the engine can produce, as an i18n key.
 *
 * A closed enum rather than free text, for three reasons: the patient app translates
 * it, the admin review queue groups by it, and free text from a rule author is free
 * text on a page a patient reads. A rule that wants to say something new adds a key
 * here and a translation in the app, in the same change.
 */
export const QUOTE_EXPLANATION_KEYS = [
    'quote.self_pay.chosen',
    'quote.self_pay.no_coverage',
    'quote.self_pay.out_of_network',
    'quote.copay_estimate.in_network',
    'quote.copay_estimate.out_of_network',
    'quote.copay_estimate.with_deductible',
    'quote.copay_estimate.coinsurance',
    'quote.copay_estimate.range',
    'quote.copay_estimate.card_on_file',
    'quote.pending_review.coverage_unknown',
    'quote.pending_review.no_benefit_reported',
    'quote.pending_review.above_threshold',
    'quote.pending_review.deductible_remaining',
    'quote.pending_review.check_failed',
    'quote.pending_review.check_unavailable',
    'quote.pending_review.no_price',
];
export const QuoteExplanationKeySchema = z.enum(QUOTE_EXPLANATION_KEYS);
/**
 * What the patient is shown, and the only thing M7 turns into money.
 *
 * `amountCents` is absent exactly when `kind === 'pending_review'`: a quote that is
 * waiting for a human has no amount, and inventing a placeholder is how a placeholder
 * gets charged. `reviewRequired` is not a synonym - a `copay_estimate` above the
 * tenant's threshold is shown to the patient *and* queued for staff.
 */
export const PriceQuoteSchema = z
    .object({
    id: z.uuid(),
    kind: PriceQuoteKindSchema,
    amountCents: z.int().min(0).max(10_000_00).nullable(),
    currency: z.string().length(3).default('USD'),
    explanationKey: QuoteExplanationKeySchema,
    reviewRequired: z.boolean(),
    /** `pricing_rules.ruleset_version` the engine ran. Stored so history stays readable. */
    rulesetVersion: z.int().nonnegative(),
    /** Only for `copay_estimate` with a `range` presentation policy. */
    rangeLowCents: z.int().min(0).nullable().default(null),
    rangeHighCents: z.int().min(0).nullable().default(null),
})
    .strict()
    .refine((value) => (value.kind === 'pending_review') === (value.amountCents === null), {
    message: 'A quote has an amount unless it is waiting for review.',
    path: ['amountCents'],
});
export const EligibilityStatusResponseSchema = z
    .object({
    data: z
        .object({
        checkId: z.uuid(),
        status: EligibilityPublicStatusSchema,
        /**
         * Patient-facing corrective hint for a `failed` check (`FR-INS-05`), as an i18n
         * key. Null when the failure is not the patient's to fix - a payer enrolment
         * problem (AAA 41) or a wrong billing NPI (43) is the clinic's.
         */
        hintKey: z.string().max(120).nullable().default(null),
        quote: PriceQuoteSchema.nullable().default(null),
        /** M15: the post-verification scheduling decision, null while pending. */
        decision: StoredDecisionSchema.nullable().default(null),
    })
        .strict(),
})
    .strict();
export const QuoteResponseSchema = z
    .object({ data: z.object({ quote: PriceQuoteSchema.nullable() }).strict() })
    .strict();
/* ------------------------------------------------------------ pricing rules ---- */
/** How a copay is presented once the engine has one (`M6.6`). */
export const COPAY_PRESENTATIONS = ['estimate', 'range', 'card_on_file'];
export const CopayPresentationSchema = z.enum(COPAY_PRESENTATIONS);
/**
 * What the engine does with a remaining deductible.
 *
 * `review` is the default and the conservative one: a patient with $1,400 of
 * deductible left owes the visit price, not the copay, and no 271 says reliably which
 * of the two this payer will apply to a telehealth visit.
 */
export const DEDUCTIBLE_HANDLINGS = ['ignore', 'add_to_copay', 'review'];
export const DeductibleHandlingSchema = z.enum(DEDUCTIBLE_HANDLINGS);
/** Which price a coinsurance percentage is applied to. */
export const COINSURANCE_BASES = ['contracted', 'self_pay'];
export const CoinsuranceBasisSchema = z.enum(COINSURANCE_BASES);
/** What to do when the payer answered but reported no in-network office-visit benefit. */
export const OUT_OF_NETWORK_POLICIES = ['self_pay', 'review'];
export const OutOfNetworkPolicySchema = z.enum(OUT_OF_NETWORK_POLICIES);
/**
 * Tenant-wide pricing settings: the half of a ruleset that is a policy rather than a
 * rule.
 *
 * Stored in `tenants.settings.pricing` and versioned with the rules it was published
 * beside (`DATABASE.md` §7.6). Keeping it out of `pricing_rules` rows is deliberate:
 * a threshold is not a condition/outcome pair, and encoding it as one would mean a
 * reserved rule row that every reader has to know to skip.
 */
export const PricingSettingsSchema = z
    .object({
    copayPresentation: CopayPresentationSchema.default('estimate'),
    /**
     * A copay estimate at or above this is still shown, and also queued for staff. `0`
     * disables the threshold; it does not mean "review everything".
     */
    reviewThresholdCents: z.int().min(0).max(10_000_00).default(15_000),
    deductibleHandling: DeductibleHandlingSchema.default('review'),
    coinsuranceBasis: CoinsuranceBasisSchema.default('contracted'),
    outOfNetworkPolicy: OutOfNetworkPolicySchema.default('review'),
    /**
     * Service type codes in preference order when reading a 271 for a telehealth
     * professional visit. `98` (physician office visit) before `30` (plan coverage),
     * because a plan-level copay is what the payer charges for *something*, not
     * necessarily for this.
     */
    telehealthStcPreference: z
        .array(z.string().regex(/^[A-Z0-9]{1,3}$/))
        .min(1)
        .max(6)
        .default(['98', '30']),
    /**
     * Self-pay price overrides by appointment type key. Absent means "use
     * `appointment_types.self_pay_price_cents`", which is where the number the booking
     * page already shows comes from - duplicating it here by default would give a
     * clinic two places to change one price.
     */
    selfPayPriceOverridesCents: z.record(z.string().max(64), z.int().min(0)).default({}),
    /** Contracted (allowed) amount by appointment type key, for coinsurance maths. */
    contractedPricesCents: z.record(z.string().max(64), z.int().min(0)).default({}),
    /** Half-width of a `range` presentation, in percent of the estimate. */
    rangeSpreadPercent: z.int().min(0).max(100).default(20),
})
    .strict();
/**
 * A rule's left-hand side. Every field is optional and an absent field means "do not
 * care"; a rule with no conditions at all is the catch-all and belongs last.
 */
export const PricingRuleConditionsSchema = z
    .object({
    appointmentTypeKey: z.string().max(64).optional(),
    coverage: z.enum(['active', 'inactive', 'unknown', 'none']).optional(),
    network: z.enum(['in_network', 'out_of_network', 'unknown']).optional(),
    hasCopay: z.boolean().optional(),
    hasCoinsurance: z.boolean().optional(),
    deductibleRemaining: z.enum(['none', 'positive']).optional(),
    payerCategory: z.enum(['commercial', 'medicare', 'medicaid', 'other']).optional(),
    /** Outcome of the eligibility check itself, independent of what it found. */
    eligibility: z.enum(['verified', 'failed', 'unavailable', 'skipped']).optional(),
})
    .strict();
/** Where a matched rule's amount comes from. */
export const AMOUNT_SOURCES = [
    'none',
    'self_pay',
    'contracted',
    'copay',
    'copay_plus_deductible',
    'coinsurance',
    'fixed',
];
export const AmountSourceSchema = z.enum(AMOUNT_SOURCES);
export const PricingRuleOutcomeSchema = z
    .object({
    kind: PriceQuoteKindSchema,
    amountSource: AmountSourceSchema,
    /** Only with `amountSource: 'fixed'`. */
    amountCents: z.int().min(0).max(10_000_00).optional(),
    reviewRequired: z.boolean().default(false),
    explanationKey: QuoteExplanationKeySchema,
})
    .strict()
    .refine((value) => (value.amountSource === 'fixed') === (value.amountCents !== undefined), {
    message: 'A fixed outcome needs an amount, and only a fixed outcome may carry one.',
    path: ['amountCents'],
})
    .refine((value) => (value.kind === 'pending_review') === (value.amountSource === 'none'), {
    message: 'A pending review has no amount source, and every other kind needs one.',
    path: ['amountSource'],
});
export const PricingRuleSchema = z
    .object({
    id: z.uuid().optional(),
    name: z.string().min(1).max(120),
    /** Lower runs first. Unique within a ruleset (`ux_pricing_rules_version_priority`). */
    priority: z.int().min(0).max(9_999),
    conditions: PricingRuleConditionsSchema,
    outcome: PricingRuleOutcomeSchema,
})
    .strict();
/** The whole ruleset as `GET /admin/pricing-rules` answers it and `PUT` accepts it. */
export const PricingRulesetSchema = z
    .object({
    rulesetVersion: z.int().nonnegative(),
    settings: PricingSettingsSchema,
    rules: z.array(PricingRuleSchema).max(100),
    publishedAt: z.iso.datetime().nullable().default(null),
    publishedBy: z.string().max(128).nullable().default(null),
})
    .strict();
export const UpdatePricingRulesRequestSchema = z
    .object({
    settings: PricingSettingsSchema,
    rules: z.array(PricingRuleSchema).max(100),
})
    .strict()
    .refine((value) => new Set(value.rules.map((rule) => rule.priority)).size === value.rules.length, {
    message: 'Two rules cannot share a priority.',
    path: ['rules'],
});
export const PricingRulesetResponseSchema = z
    .object({ data: z.object({ ruleset: PricingRulesetSchema }).strict() })
    .strict();
/* -------------------------------------------------------------- billing NPIs ---- */
/**
 * A billing NPI and its CMS attestation (`FR-INS-06`).
 *
 * The attestation is an instant rather than a boolean because CMS requires it to be
 * renewed, and "attested" with no date is a claim nobody can audit. An absent
 * `cmsAttestedAt` refuses every Medicare check on this NPI, which is the behaviour the
 * requirement asks for.
 */
export const BillingNpiSchema = z
    .object({
    id: z.uuid(),
    npi: z.string().regex(/^\d{10}$/),
    organizationName: z.string().min(1).max(200),
    isDefault: z.boolean(),
    isActive: z.boolean(),
    cmsAttestedAt: z.iso.datetime().nullable(),
    cmsAttestedBy: z.string().max(128).nullable(),
    createdAt: z.iso.datetime(),
})
    .strict();
export const CreateBillingNpiRequestSchema = z
    .object({
    npi: z.string().regex(/^\d{10}$/, 'An NPI is ten digits.'),
    organizationName: z.string().min(1).max(200),
    isDefault: z.boolean().default(false),
})
    .strict();
export const UpdateBillingNpiRequestSchema = z
    .object({
    organizationName: z.string().min(1).max(200).optional(),
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
    /**
     * `true` records the attestation now with the acting user; `false` withdraws it.
     * The client never sends an instant: an attestation dated by its claimant is not
     * an attestation.
     */
    cmsAttested: z.boolean().optional(),
})
    .strict()
    .refine((value) => Object.keys(value).length > 0, { message: 'Send at least one field.' });
export const BillingNpiListResponseSchema = z.object({ data: z.array(BillingNpiSchema) }).strict();
export const BillingNpiResponseSchema = z
    .object({ data: z.object({ billingNpi: BillingNpiSchema }).strict() })
    .strict();
/* ------------------------------------------------------------- review tasks ---- */
/** `review_tasks.kind` (`DATABASE.md` §7.6). */
export const REVIEW_TASK_KINDS = [
    'price_review',
    'eligibility_failed',
    'ehr_failed',
    'charge_failed',
    'undeliverable_contact',
    'manual_completion',
    'late_cancel_fee',
    /** M15: the decision engine told a patient to contact the office; staff call back. */
    'scheduling_blocked',
];
export const ReviewTaskKindSchema = z.enum(REVIEW_TASK_KINDS);
export const REVIEW_TASK_STATUSES = ['open', 'in_progress', 'resolved', 'dismissed'];
export const ReviewTaskStatusSchema = z.enum(REVIEW_TASK_STATUSES);
/**
 * A queue row. Carries ids and a status, never a patient's name: the queue is a list
 * of work, and the staff member who opens one follows the appointment link to see who
 * it is about, which is the read that gets audited.
 */
export const ReviewTaskSchema = z
    .object({
    id: z.uuid(),
    kind: ReviewTaskKindSchema,
    status: ReviewTaskStatusSchema,
    appointmentId: z.uuid().nullable(),
    patientId: z.uuid().nullable(),
    priceQuoteId: z.uuid().nullable(),
    chargeIntentId: z.uuid().nullable(),
    /** M15: the draft a `scheduling_blocked` task is about (an id; staff open it to call back). */
    draftId: z.uuid().nullable().default(null),
    /**
     * M15: de-identified facts about why the task exists. `scheduling_blocked` carries
     * `{ messageKey, rulesetVersion }`. Never a name, a contact or a member id.
     */
    details: z
        .record(z.string().max(64), z.union([z.string().max(200), z.number(), z.boolean()]))
        .nullable()
        .default(null),
    assignedToUserId: z.string().max(128).nullable(),
    dueAt: z.iso.datetime().nullable(),
    resolution: z.string().max(500).nullable(),
    resolvedBy: z.string().max(128).nullable(),
    resolvedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
})
    .strict();
export const ReviewTaskQuerySchema = z
    .object({
    kind: ReviewTaskKindSchema.optional(),
    status: ReviewTaskStatusSchema.optional(),
    assignedToUserId: z.string().max(128).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().max(200).optional(),
})
    .strict();
export const ReviewTaskListResponseSchema = z
    .object({
    data: z.array(ReviewTaskSchema),
    nextCursor: z.string().nullable().default(null),
})
    .strict();
export const ReviewTaskResponseSchema = z
    .object({ data: z.object({ reviewTask: ReviewTaskSchema }).strict() })
    .strict();
/**
 * Resolving a task needs a reason, and the reason is required rather than encouraged.
 *
 * Every action in this group writes an audit row whose whole value is the sentence a
 * human typed about why the amount is what it is (`rules` §33). An optional reason is
 * a reason that is absent on the row somebody eventually has to explain.
 */
export const ResolveReviewTaskRequestSchema = z
    .object({
    status: z.enum(['in_progress', 'resolved', 'dismissed']),
    reason: z.string().min(3).max(500),
    assignedToUserId: z.string().max(128).nullable().optional(),
})
    .strict();
export const SetQuoteAmountRequestSchema = z
    .object({
    amountCents: z.int().min(0).max(10_000_00),
    reason: z.string().min(3).max(500),
})
    .strict();
export const MarkSelfPayRequestSchema = z.object({ reason: z.string().min(3).max(500) }).strict();
export const RerunEligibilityRequestSchema = z
    .object({ reason: z.string().min(3).max(500).optional() })
    .strict();
export const RerunEligibilityResponseSchema = z
    .object({
    data: z.object({ checkId: z.uuid(), status: EligibilityPublicStatusSchema }).strict(),
})
    .strict();
/* ------------------------------------------------------------- AI estimate ---- */
/**
 * The de-identified input that leaves the platform (`AI_SYSTEM_SPEC.md` §5).
 *
 * `.strict()` is the enforcement, not a convenience: the specification lists the keys
 * that are forbidden - name, date of birth, member id, payer id, appointment id, raw
 * X12 - and the way to guarantee they never appear is to reject every key that is not
 * named here. A future field is a schema change reviewed against §5, which is exactly
 * the review it should get.
 */
export const DeidentifiedBenefitLineSchema = z
    .object({
    serviceTypeCode: z.string().regex(/^[A-Z0-9]{1,3}$/),
    /** X12 `EB01`: `A` coinsurance, `B` copay, `C` deductible, `G` out-of-pocket. */
    code: z.enum(['A', 'B', 'C', 'G']),
    amountCents: z.int().min(0).max(100_000_00).optional(),
    /** `0.2` for 20 percent. */
    percent: z.number().min(0).max(1).optional(),
    /** X12 `timeQualifierCode`, e.g. `29` = remaining. */
    timeQualifier: z.string().max(3).optional(),
    inNetwork: z.boolean().optional(),
})
    .strict();
export const DeidentifiedPriceRowSchema = z
    .object({
    visitType: z.string().max(64),
    selfPayPriceCents: z.int().min(0),
    contractedPriceCents: z.int().min(0).optional(),
})
    .strict();
export const DeidentifiedBenefitsSchema = z
    .object({
    coverageActive: z.boolean(),
    payerCategory: z.enum(['commercial', 'medicare', 'medicaid', 'other']),
    visitType: z.string().max(64),
    placeOfService: z.literal('telehealth'),
    networkStatus: z.enum(['in_network', 'out_of_network', 'unknown']),
    benefits: z.array(DeidentifiedBenefitLineSchema).max(50),
    /**
     * Payer free text, scrubbed and capped at 2,000 characters in total
     * (`AI_SYSTEM_SPEC.md` §8). Untrusted: it is delimited as data in the prompt and
     * never concatenated into the instructions.
     */
    planMessages: z.array(z.string().max(500)).max(20),
    priceTable: z.array(DeidentifiedPriceRowSchema).max(20),
})
    .strict();
/** `AI_SYSTEM_SPEC.md` §6: the flags a model may raise, as a closed enum. */
export const COPAY_ESTIMATE_FLAGS = [
    'no_stc98_benefit',
    'coinsurance_applied',
    'deductible_remaining_present',
    'out_of_network',
    'coverage_inactive',
    'telehealth_excluded',
    'conflicting_benefits',
    'price_table_missing',
    'suspicious_plan_text',
    'low_data_quality',
];
export const CopayEstimateFlagSchema = z.enum(COPAY_ESTIMATE_FLAGS);
/**
 * Flags that hide the estimate from patients and badge it for staff
 * (`AI_SYSTEM_SPEC.md` §6). Each one means the model itself said the input was not
 * good enough to price from.
 */
export const BLOCKING_COPAY_ESTIMATE_FLAGS = Object.freeze([
    'coverage_inactive',
    'telehealth_excluded',
    'conflicting_benefits',
    'suspicious_plan_text',
    'price_table_missing',
]);
/**
 * What a model is allowed to have said (`AI_SYSTEM_SPEC.md` §6).
 *
 * Model output is an untrusted generated artifact (rules §55.11), so every field is
 * bounded: the amount cannot exceed $10,000, the summary is length-capped and refuses
 * control characters and markup, and `flags` is an enum. Anything else is
 * `invalid_output` and the estimate is `unavailable` - never a coerced best effort.
 */
export const CopayEstimateSchema = z
    .object({
    estimatedPatientResponsibilityCents: z.int().min(0).max(10_000_00),
    confidence: z.number().min(0).max(1),
    reasoningSummary: z
        .string()
        .min(1)
        .max(500)
        // eslint-disable-next-line no-control-regex -- control characters are the point
        .refine((value) => !/[\u0000-\u001f<>]/.test(value), {
        message: 'The summary must be plain text.',
    }),
    flags: z.array(CopayEstimateFlagSchema).max(10).default([]),
})
    .strict();
/** `ai_estimates.status`. */
export const AI_ESTIMATE_STATUSES = [
    'ok',
    'invalid_output',
    'error',
    'timeout',
    'cap_reached',
    'skipped',
];
export const AiEstimateStatusSchema = z.enum(AI_ESTIMATE_STATUSES);
/** What staff see next to the rule-engine result. */
export const AiEstimateSchema = z
    .object({
    id: z.uuid(),
    status: AiEstimateStatusSchema,
    estimateCents: z.int().min(0).nullable(),
    confidence: z.number().min(0).max(1).nullable(),
    reasoningSummary: z.string().max(500).nullable(),
    flags: z.array(CopayEstimateFlagSchema),
    /** True when a blocking flag or a low confidence keeps this away from patients. */
    patientVisible: z.boolean(),
    model: z.string().max(120),
    promptVersion: z.string().max(40),
    latencyMs: z.int().nullable(),
    createdAt: z.iso.datetime(),
})
    .strict();
export const AiEstimateResponseSchema = z
    .object({ data: z.object({ aiEstimate: AiEstimateSchema.nullable() }).strict() })
    .strict();
/** The catalog keys the in-repo AI adapters register under (`M6.7`). */
export const AI_PROVIDERS = ['ai-openai', 'ai-anthropic', 'ai-google'];
export const AiProviderSchema = z.enum(AI_PROVIDERS);
/**
 * Tenant AI settings (`M6.11`), under `tenants.settings.ai`.
 *
 * `model` is a free string rather than an enum on purpose (rules §57.11): model ids
 * change faster than releases, and a clinic that has to wait for a deploy to move from
 * one Sonnet to the next is a clinic that stays on the old one.
 */
export const AiSettingsSchema = z
    .object({
    provider: AiProviderSchema.default('ai-anthropic'),
    model: z.string().min(1).max(120).default('claude-sonnet-5'),
    /** Estimates per calendar month per tenant. `0` disables the feature outright. */
    monthlyCap: z.int().min(0).max(100_000).default(500),
    /** Show the estimate to patients too. Default off (`AI_SYSTEM_SPEC.md` §2). */
    patientVisible: z.boolean().default(false),
    /** Below this, an estimate is never shown to a patient. */
    patientConfidenceThreshold: z.number().min(0).max(1).default(0.7),
})
    .strict();
export const AiSettingsResponseSchema = z
    .object({ data: z.object({ ai: AiSettingsSchema }).strict() })
    .strict();
export const UpdateAiSettingsRequestSchema = AiSettingsSchema.partial()
    .strict()
    .refine((value) => Object.keys(value).length > 0, { message: 'Send at least one field.' });
/* ------------------------------------------------------------ rule engine ---- */
/**
 * Everything the engine is allowed to look at.
 *
 * De-identified by construction: an appointment type key, a handful of amounts and
 * three enums. The engine cannot reach a patient record even by accident, which is
 * what makes `price_quotes.inputs` safe to store verbatim and safe to show a reviewer.
 */
export const QuoteInputSchema = z
    .object({
    appointmentTypeKey: z.string().min(1).max(64),
    selfPayPriceCents: z.int().min(0),
    contractedPriceCents: z.int().min(0).optional(),
    /** `none` means the patient chose self-pay; `unknown` means the payer did not say. */
    coverage: z.enum(['active', 'inactive', 'unknown', 'none']),
    eligibility: z.enum(['verified', 'failed', 'unavailable', 'skipped']),
    network: z.enum(['in_network', 'out_of_network', 'unknown']).default('unknown'),
    copayCents: z.int().min(0).optional(),
    coinsuranceRate: z.number().min(0).max(1).optional(),
    deductibleRemainingCents: z.int().min(0).optional(),
    payerCategory: z.enum(['commercial', 'medicare', 'medicaid', 'other']).optional(),
})
    .strict();
//# sourceMappingURL=insurance.js.map